import { useIsFocused } from '@react-navigation/native';
import { isMobileNative } from 'app/utils/web-helpers';
import type { ReactNode } from 'react';
import { useLayoutEffect } from 'react';
import Animated, {
  Easing,
  ReduceMotion,
  interpolate,
  useAnimatedStyle,
  useReducedMotion,
  useSharedValue,
  withSpring,
  withTiming,
} from 'react-native-reanimated';

const TAB_FADE_DURATION = 140;
const TAB_TRANSITION_OFFSET = 8;

/**
 * Gives a newly focused tab a short, native-driven entrance without changing
 * React Navigation's tab lifecycle. This keeps each tab's navigator and scroll
 * position intact while avoiding a hard scene swap.
 */
export function TabSceneTransition({ children }: Readonly<{ children: ReactNode }>) {
  const isFocused = useIsFocused();
  const prefersReducedMotion = useReducedMotion();
  const opacity = useSharedValue(0);
  const motionProgress = useSharedValue(0);

  useLayoutEffect(() => {
    if (!isMobileNative || prefersReducedMotion) {
      opacity.set(1);
      motionProgress.set(1);
      return;
    }

    opacity.set(
      isFocused
        ? withTiming(1, {
            duration: TAB_FADE_DURATION,
            easing: Easing.out(Easing.cubic),
            reduceMotion: ReduceMotion.System,
          })
        : 0,
    );
    motionProgress.set(
      isFocused
        ? withSpring(1, {
            damping: 22,
            mass: 0.7,
            overshootClamping: true,
            reduceMotion: ReduceMotion.System,
            stiffness: 220,
          })
        : 0,
    );
  }, [isFocused, motionProgress, opacity, prefersReducedMotion]);

  const animatedStyle = useAnimatedStyle(() => ({
    opacity: opacity.get(),
    transform: [
      { translateY: interpolate(motionProgress.get(), [0, 1], [TAB_TRANSITION_OFFSET, 0]) },
      { scale: interpolate(motionProgress.get(), [0, 1], [0.992, 1]) },
    ],
  }));

  if (!isMobileNative) {
    return children;
  }

  return (
    <Animated.View collapsable={false} pointerEvents={isFocused ? 'auto' : 'none'} style={[{ flex: 1 }, animatedStyle]}>
      {children}
    </Animated.View>
  );
}
